import { inject, Injectable } from "@angular/core";

import { AttachmentTargetEnum, AttachmentTypeEnum } from "@shared/enums";
import {
  IAttachment,
  ICertificateExtended,
  IDocumentExtended,
  ICertificate,
  IDocument,
} from "@shared/interfaces";
import {
  AuthenticationService,
  AttachmentsService,
  CertificatesService,
  DocumentsService,
  NotificationService,
} from "@shared/services";
import { CommonUtils } from "@shared/utils";

@Injectable({
  providedIn: "root",
})
export class AttachmentsCommonService {
  private readonly attachmentsService = inject(AttachmentsService);

  private readonly authenticationService = inject(AuthenticationService);

  private readonly certificatesService = inject(CertificatesService);

  private readonly documentsService = inject(DocumentsService);

  private readonly notificationService = inject(NotificationService);

  async loadSelectedAttachments(
    attachmentType: AttachmentTypeEnum,
    attachmentTarget: AttachmentTargetEnum,
    targetId: string,
    isReturnOnlyAttachments?: boolean,
  ): Promise<ICertificate[] | IDocument[] | IAttachment[]> {
    try {
      const activeOrganisationId = this.authenticationService.getActiveOrganisationId();

      const attachments = await this.attachmentsService.getAll(
        attachmentType,
        CommonUtils.getTargetUri(activeOrganisationId, attachmentTarget, targetId),
        null,
      );

      if (isReturnOnlyAttachments) {
        return attachments;
      }

      if (!attachments?.length) {
        return [];
      }

      let result = [];

      if (attachmentType === AttachmentTypeEnum.CERTIFICATE) {
        const cetificatesIds = attachments.map((attachment) =>
          CommonUtils.getUriId(attachment["attachmentUri"]),
        );

        const certificates = await this.certificatesService.getByIdsGraphQL(cetificatesIds);

        result = certificates.map((certificate: ICertificateExtended) => ({
          ...certificate,
          linkId: attachments.find(
            (attachment) => CommonUtils.getUriId(attachment["attachmentUri"]) === certificate.id,
          ).id,
        })) as ICertificate[];
      } else if (attachmentType === AttachmentTypeEnum.DOCUMENT) {
        const documentsIds = attachments.map((attachment) =>
          CommonUtils.getUriId(attachment["attachmentUri"]),
        );

        const documents = await this.documentsService.getByIdsGraphQL(documentsIds);

        result = documents.map((document: IDocumentExtended) => ({
          id: document.id,
          name: document.name,
          type: document.type.name,
          contentType: document.contentType,
          validityStart: document.validityStart,
          validityEnd: document.validityEnd,
          recordState: document.recordState,
          linkId: attachments.find(
            (attachment) => CommonUtils.getUriId(attachment["attachmentUri"]) === document.id,
          ).id,
        })) as any[];
      }

      return result;
    } catch (error) {
      this.notificationService.showError(error);

      return Promise.reject(error);
    }
  }
}
